import { Divider, IconButton } from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import { FormikCheckboxField, FormikNumberField } from '@superdispatch/forms';
import { Inline, Stack } from '@superdispatch/ui';
import { Button, TextBox } from '@superdispatch/ui-lab';
import { FieldArray, useField, useFormikContext } from 'formik';
import { useState } from 'react';
import styled from 'styled-components';
import {
  CarrierPayVehicleDTO,
  carrierPayVehicleSchema,
  generateVehicleKey,
} from '../../../data/ShippingDetailsDTO';
import { VehicleMakeDTO } from '../../../data/VehicleDTO';
import { VehicleMakeAutocompleteField } from './VehicleMakeAutocompleteField';
import { VehicleModelAutocompleteField } from './VehicleModelAutocompleteField';
import { VehicleTypeField } from './VehicleTypeField';

const FieldsContainer = styled.div`
  display: grid;
  grid-column-gap: 8px;
  grid-template-columns: 100px 114px 176px 124px 34px;

  @media (max-width: 450px) {
    grid-row-gap: 8px;
    grid-template-columns: 1fr;
  }

  @media (min-width: 451px) and (max-width: 1340px) {
    grid-row-gap: 8px;
    grid-template-columns: repeat(3, minmax(80px, 1fr));

    & :nth-child(2) {
      grid-column: 2 / span 3;
    }
    & :nth-child(3) {
      grid-column: 1 / span 2;
    }

    & :nth-child(5) {
      grid-row: 3;
    }
  }
`;

interface VehicleItemFieldProps {
  index: number;
  vehicle: CarrierPayVehicleDTO;
  showDivider: boolean;
  name: string;
  disabled: boolean;
  onRemove: () => void;
}

function VehicleItemField({
  index,
  name,
  onRemove,
  showDivider,
  vehicle,
  disabled,
}: VehicleItemFieldProps) {
  const [make, setMake] = useState<VehicleMakeDTO | null>(null);

  return (
    <Stack>
      {showDivider && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            margin: '2px 0',
          }}
        >
          <TextBox color="secondary" variant="body-semibold">
            #{index + 1}
          </TextBox>
          <Divider style={{ flexGrow: 1 }} />
          <IconButton disabled={disabled} onClick={onRemove}>
            <Delete />
          </IconButton>
        </div>
      )}

      <FieldsContainer>
        <FormikNumberField
          name={`${name}.year`}
          inputMode="numeric"
          disabled={disabled}
          label={
            <Inline noWrap={true} space="xxsmall">
              <TextBox>Year</TextBox>
              <TextBox color="secondary">(Required)</TextBox>
            </Inline>
          }
          inputProps={{
            decimalScale: 0,
            thousandSeparator: false,
            allowNegative: false,
            allowLeadingZeros: false,
          }}
        />

        <VehicleMakeAutocompleteField
          name={`${name}.make`}
          disabled={disabled || !vehicle.year}
          onChange={(value: VehicleMakeDTO) => {
            setMake(value);
          }}
        />

        <VehicleModelAutocompleteField
          name={name}
          make={vehicle.make}
          makeId={make?.id}
          disabled={disabled || !vehicle.year || !vehicle.make}
        />

        <VehicleTypeField
          name={`${name}.type`}
          make={vehicle.make}
          disabled={
            disabled || !vehicle.year || !vehicle.make || !vehicle.model
          }
        />

        <Stack space="xxsmall">
          <TextBox>INOP</TextBox>
          <FormikCheckboxField
            name={`${name}.is_inoperable`}
            label=""
            disabled={disabled}
          />
        </Stack>
      </FieldsContainer>
    </Stack>
  );
}

interface VehiclesFieldProps {
  name: string;
  disabled: boolean;
}

export function VehiclesField({ name, disabled }: VehiclesFieldProps) {
  const { isSubmitting } = useFormikContext();

  const [
    {
      value = [
        carrierPayVehicleSchema.cast({
          model: null,
          make: null,
          type: null,
        }),
      ],
    },
  ] = useField<CarrierPayVehicleDTO[]>(name);
  return (
    <FieldArray name={name}>
      {({ remove, push }) => {
        return (
          <Stack space="small">
            <Stack space="medium">
              {value.map((item, index) => (
                <VehicleItemField
                  key={item.$key}
                  vehicle={item}
                  index={index}
                  showDivider={value.length > 1}
                  name={`${name}.${index}`}
                  disabled={disabled}
                  onRemove={() => {
                    remove(index);
                  }}
                />
              ))}
            </Stack>

            {value.length <= 10 && (
              <Button
                variant="text"
                startIcon={<Add />}
                disabled={disabled || isSubmitting}
                onClick={() => {
                  push({
                    type: null,
                    model: null,
                    make: null,
                    year: null,
                    $key: generateVehicleKey(),
                  });
                }}
              >
                Add Vehicle
              </Button>
            )}
          </Stack>
        );
      }}
    </FieldArray>
  );
}
